"use client";
import React, { useState } from "react";
import Modal from "../ModalContainer";
import { ContactUsProps } from "./types";
import { sendCf7Form } from "@/src/api/sendCf7Form/sendCf7Form";
import { Logo } from "../header/components";

const ContactUs: React.FC<ContactUsProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
  };

  const validateForm = () => {
    const { firstName, lastName, email, message } = formData;
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !message.trim()
    ) {
      setErrorMsg("Please fill in all fields.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const result = await sendCf7Form(formData);
      if (result.status) {
        setFormData({ firstName: "", lastName: "", email: "", message: "" });
        setIsSubmitted(true);

        setTimeout(() => {
          onClose();
          setIsSubmitted(false);
        }, 5000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerText = isSubmitted
    ? "Thank you for the information you provided. We will be in touch with you shortly."
    : "Reach out via email, and we will return your message as soon as possible.";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {!isSubmitted ? (
        <div className="flex items-center flex-col justify-center overflow-auto">
          <form
            onSubmit={handleSubmit}
            className="space-y-4 w-full max-w-[90%] md:p-16 bg-white h-full"
          >
            <div className="text-center">
              <Logo center={true} noRedirect={true} />
            </div>
            <div className="text-center">
              <p className="contact-modal-header">GET IN TOUCH</p>
              <p className="contact-modal-subheader">{headerText}</p>
            </div>

            <div className="flex items-center justify-center w-full">
              <div
                className="
                  space-y-4 w-full max-w-[90%] md:max-w-[622px] 
                  p-4 md:p-16 bg-white border border-gray-500"
              >
                <p className="contact-form-title">
                  PLEASE PROVIDE YOUR INFORMATION
                </p>
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    className="input-field"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    className="input-field"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  className="input-field"
                  value={formData.email}
                  onChange={handleChange}
                />

                <textarea
                  name="message"
                  placeholder="Your message"
                  className="input-field"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                />

                <button
                  type="submit"
                  className={`contact-form-button ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "SUBMIT"}
                </button>

                {errorMsg && (
                  <p className="mt-2 text-red-500 text-sm sm:text-base leading-tight">
                    {errorMsg}
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex items-center flex-col justify-center overflow-auto ">
          <p className="text-2xl font-bold text-green-600">Thank you!</p>
          <p className="text-lg text-gray-600 text-center">
            Your message has been successfully sent. We will contact you
            shortly.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default ContactUs;
